import React, { useEffect, useState } from 'react';
import styled, { keyframes } from 'styled-components';
import Sound from 'react-sound';
import { useImagesContext } from '../../contexts/ImagesContext';
import { ImgClick } from '../utility';
import { getSkyPlatform, updateSkyPlatform } from '../../services/sky-platform';
import { connect } from 'react-redux';
import UIFx from 'uifx';

import backgroundSound from './sounds/background.mp3';
import walkSoundFile from './sounds/walk.mp3';
import dicingFile from './sounds/dicing.mp3';
import gotenPolar4Part1File from './sounds/goten-polar4-1.wav';
import gotenPolar4Part2File from './sounds/goten-polar4-2.mp3';
import openChestFile from './sounds/open-chest.mp3';
import getYardnaphaFile from './sounds/get-yardnapha.mp3';
import { useShortcut } from '../../utils/useShortcut';

const walkSound = new UIFx(walkSoundFile);
const dicingSound = new UIFx(dicingFile);
const gotenPolar4Part1Sound = new UIFx(gotenPolar4Part1File);
const gotenPolar4Part2Sound = new UIFx(gotenPolar4Part2File);
const openChestSound = new UIFx(openChestFile);

const POINT_TYPE = {
  bonus: 'bonus',
  treasure_chest: 'treasure_chest',
  torch: 'torch',
};

const WALK_POINT = [
  { left: 128, top: 917, type: POINT_TYPE.bonus },
  { left: 137, top: 672, type: null },
  { left: 113, top: 427, type: POINT_TYPE.torch },
  { left: 100, top: 205, type: POINT_TYPE.treasure_chest },
  { left: 227, top: 64, type: null },
  { left: 457, top: 70, type: POINT_TYPE.bonus },
  { left: 681, top: 59, type: null },
  { left: 909, top: 104, type: POINT_TYPE.torch },
  { left: 1115, top: 38, type: POINT_TYPE.treasure_chest },
  { left: 1348, top: 77, type: null },
  { left: 1589, top: 64, type: POINT_TYPE.bonus },
  { left: 1790, top: 140, type: null },
  { left: 1819, top: 408, type: POINT_TYPE.torch },
  { left: 1790, top: 599, type: null },
  { left: 1694, top: 747, type: POINT_TYPE.treasure_chest },
  { left: 1488, top: 798, type: null },
  { left: 1284, top: 896, type: null },
  { left: 1073, top: 885, type: POINT_TYPE.torch },
  { left: 865, top: 915, type: null },
  { left: 645, top: 875, type: POINT_TYPE.treasure_chest },
  { left: 440, top: 931, type: null },
];

const STATE = {
  rolling: 'rolling',
  walking: 'walking',
  ready: 'ready',
  add_amulet: 'add_amulet',
  add_hidden_power: 'add_hidden_power',
  add_yardnapha: 'add_yardnapha',
  show_amulet: 'show_amulet',
  yardnapha_runout: 'yardnapha_runout',
};

const delay = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const SkyPlatform = ({ history, student, config }) => {
  const { images } = useImagesContext();
  const [diceNumber, setDiceNumber] = useState(null);
  const [state, setState] = useState([STATE.ready]);
  const [mapPointIndex, setMapPointIndex] = useState(0);
  const [yardnapha, setYardnapha] = useState(null);
  const [addHiddenPower, setAddHiddenPower] = useState(null);
  const [addAmuletId, setAddAmuletId] = useState(null);
  const [addYardnapha, setAddYardnapha] = useState(null);
  const [torch, setTorch] = useState(null);
  const [torchFull, setTorchFull] = useState(null);
  // const [walkCount, setWalkCount] = useState(0);

  useShortcut('2', () => {
    rollTheDice();
  });

  console.debug('SkyPlatform:state', state);

  async function walk(
    walkCount,
    addAmuletId,
    addYardnapha,
    torch,
    torchFull,
    yardnapha,
  ) {
    let tmpMapPointIndex = mapPointIndex;
    for (let i = 0; i < walkCount; i++) {
      console.debug('SkyPlatform:walk', i, mapPointIndex);
      if (!config.isMutedSound) walkSound.play();
      if (tmpMapPointIndex + 1 >= WALK_POINT.length) {
        tmpMapPointIndex = 0;
      } else {
        tmpMapPointIndex += 1;
      }
      setMapPointIndex(tmpMapPointIndex);
      await delay(500);
    }
    console.debug(`torch: ${torch}`);
    // console.debug(`type: ${WALK_POINT[tmpMapPointIndex].type}`);
    if (torch !== null) {
      setTorch(torch);
    }
    if (torchFull) {
      setTorchFull(torchFull);
      setTimeout(async () => {
        if (!config.isMutedSound) {
          gotenPolar4Part1Sound.play();
        }
      }, 0);
      setTimeout(async () => {
        if (!config.isMutedSound) {
          gotenPolar4Part2Sound.play();
        }
      }, 2500);

      await delay(5000);
      setTorchFull(null);
    }
    if (addAmuletId) {
      setState([STATE.add_amulet]);
      if (!config.isMutedSound) {
        openChestSound.play();
      }
      await delay(3500);
      setState((v) => [...v, STATE.show_amulet]);
      await delay(1500);
    }
    if (addYardnapha) {
      setState([STATE.add_yardnapha]);
      await delay(2000);
      setYardnapha(yardnapha + addYardnapha);
    }
    setState([STATE.ready]);
    setDiceNumber(null);
  }

  async function rollTheDice() {
    if (state.find((v) => v === STATE.ready)) {
      if (yardnapha <= 0) {
        setState([STATE.yardnapha_runout]);
        await delay(2000);
        setState([STATE.ready]);
        return;
      }
      // const tempDiceNumber = Math.floor(Math.random() * 6) + 1;
      if (!config.isMutedSound) dicingSound.play();
      updateSkyPlatform(student.id, mapPointIndex).then((res) => {
        console.debug('SkyPlatform:updateSkyPlatform', res.data);
        const {
          diceNumber,
          skyPlatformPoint,
          yardnapha,
          addHiddenPower,
          addAmuletId,
          addYardnapha,
          skyPlatformTorch,
          torchFull,
        } = res.data;
        setYardnapha(yardnapha);
        // console.debug('yardnapha', yardnapha);
        if (!diceNumber) {
          setMapPointIndex(skyPlatformPoint);
          return;
        }
        setAddAmuletId(addAmuletId);
        setAddHiddenPower(addHiddenPower);
        setAddYardnapha(addYardnapha);
        setState([STATE.rolling]);
        setDiceNumber(diceNumber);
        setTimeout(() => {
          setState((s) => [...s, STATE.walking]);
          walk(
            diceNumber,
            addAmuletId,
            addYardnapha,
            skyPlatformTorch,
            torchFull,
            yardnapha,
          );
        }, 1500);
      });
    }
  }

  function getDiceImage(diceNumber) {
    if (diceNumber >= 1 && diceNumber <= 6) {
      return images[`dice-${diceNumber}.png`];
    }
    return null;
  }

  useEffect(() => {
    getSkyPlatform(student.id).then((res) => {
      console.debug('SkyPlatform:getSkyPlatform', res.data);
      setMapPointIndex(res.data.skyPlatformPoint);
      setYardnapha(res.data.yardnapha);
      setTorch(res.data.skyPlatformTorch);
    });
  }, []);

  function getAddMuletIdImage() {
    if (state.find((v) => v === STATE.add_amulet)) {
      return images[addAmuletId <= 6 ? 'amulet-normal.png' : 'amulet-rare.png'];
    }
    return null;
  }

  function getAddYardnapha() {
    if (state.find((v) => v === STATE.add_yardnapha)) {
      if (addYardnapha === 1) {
        return images['add-one.png'];
      } else if (addYardnapha === 2) {
        return images['add-two.png'];
      }
    }
    return null;
  }

  function getAmuletImage() {
    if (state.find((v) => v === STATE.show_amulet)) {
      return images[`amulet_${String(addAmuletId).padStart(2, '0')}.png`];
    }
    return null;
  }

  return (
    <Container>
      <Background src={images['background.jpg']}></Background>
      {(torch & 1) > 0 && <Torch1 src={images['torch-on.png']} />}
      {(torch & 2) > 0 && <Torch2 src={images['torch-on.png']} />}
      {(torch & 4) > 0 && <Torch3 src={images['torch-on.png']} />}
      {(torch & 8) > 0 && <Torch4 src={images['torch-on.png']} />}

      <RollingDice
        $zIndex={state.find((v) => v === STATE.rolling) ? 1 : 0}
        src={getDiceImage(diceNumber)}
      />
      <PlayBlock $background={images['play-bg.png']} $width={582} $height={202}>
        <Home onClick={() => history.goBack()} src={images['home.png']} />
        <Dice onClick={rollTheDice} src={images['dice.png']} />
        <YardnaphaBlock>
          <YardnaphaBlockText>
            {yardnapha && yardnapha > 0 ? `${yardnapha}` : '-'}
          </YardnaphaBlockText>
        </YardnaphaBlock>
      </PlayBlock>
      <TuagomTag
        src={images['walk-point.png']}
        $left={WALK_POINT[mapPointIndex].left}
        $top={WALK_POINT[mapPointIndex].top}
      />
      <TorchFull src={torchFull ? images['torch-full.png'] : null} />
      <AddAmuletId src={getAddMuletIdImage()} />
      {/* <AddAmuletId src={images['amulet-normal.png']} /> */}
      <Amulet src={getAmuletImage()} />
      {state.find((v) => v === STATE.add_yardnapha) && addYardnapha > 0 && (
        <Sound
          url={getYardnaphaFile}
          playStatus="PLAYING"
          loop={false}
          volume={config.isMutedSound ? 0 : 100}
        ></Sound>
      )}

      <AddYardnapha src={getAddYardnapha()} />
      {state.find((v) => v === STATE.yardnapha_runout) && (
        <YardnaphaRunout src={images['yardnapha-runout.png']} />
      )}
      <Sound
        url={backgroundSound}
        playStatus="PLAYING"
        loop={true}
        volume={config.isMutedSound ? 0 : 100}
      ></Sound>

      {/* <TuagomTag
        src={
          state.find((v) => v === STATE.walking)
            ? images['walk-point.png']
            : null
        }
        $left={WALK_POINT[mapPointIndex].left}
        $top={WALK_POINT[mapPointIndex].top}
      /> */}
      {/* {WALK_POINT.map((point, index) => {
        return (
          <TuagomTag
            key={index}
            src={images['walk-point.png']}
            $left={point.left}
            $top={point.top}
          />
        );
      })} */}
    </Container>
  );
};

const AddYardnapha = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
`;

const TorchFull = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
`;

const YardnaphaRunout = styled.img`
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const Torch1 = styled.img`
  position: absolute;
  left: 14px;
  top: 340px;
`;
const Torch2 = styled.img`
  position: absolute;
  left: 808px;
  top: 0px;
`;
const Torch3 = styled.img`
  position: absolute;
  right: 12px;
  top: 270px;
`;
const Torch4 = styled.img`
  position: absolute;
  left: 977px;
  bottom: 28px;
`;

const AddAmuletId = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
`;

const Amulet = styled.img`
  position: absolute;
  top: 220px;
  left: 930px;
`;

const YardnaphaBlock = styled.div`
  position: absolute;
  bottom: 0px;
  right: 25px;
  width: 150px;
  height: 150px;
  // border: 1px solid #000;
`;

const YardnaphaBlockText = styled.span`
  font-family: PSLxText;
  font-weight: bold;
  font-size: 100px;
  color: white;
  paint-order: stroke fill;
  -webkit-text-stroke: 0.05em black;
  position: absolute;
  top: 50%;
  left: 50%;
  transform: translate(-50%, -50%);
`;

const fadeIn = keyframes`
  from {
    opacity: 0;
  }
  to {
    opacity: 1;
  }
`;

const Container = styled.div`
  position: relative;
  width: 1920px;
  height: 1080px;
`;

const Background = styled.img`
  top: 0px;
  left: 0px;
  width: 1920px;
  height: 1080px;
  position: absolute;
  object-fit: contain;
`;

const PlayBlock = styled.div`
  position: absolute;
  bottom: 0px;
  right: 0px;
  background-image: url(${(props) => props.$background});
  width: ${(props) => props.$width}px;
  height: ${(props) => props.$height}px;
`;

const Home = styled(ImgClick)`
  position: absolute;
  left: 75px;
  bottom: 0px;
`;

const Dice = styled(ImgClick)`
  position: absolute;
  left: 240px;
  bottom: 0px;
`;

const RollingDice = styled.img`
  position: absolute;
  top: 0px;
  left: 0px;
  z-index: ${(props) => (props.$zIndex ? props.$zIndex : null)};
`;

const TuagomTag = styled.img`
  position: absolute;
  left: ${(props) => props.$left}px;
  top: ${(props) => props.$top}px;
  width: 171px;
  height: 185px;
  transform: translate(-85px, -92px);
  // border: 1px solid #000;
  animation: ${fadeIn} 1s ease-in-out;
`;

const mapStateToProps = ({ student, config }) => ({
  student,
  config,
});

const mapDispatchToProps = {};

export default connect(mapStateToProps, mapDispatchToProps)(SkyPlatform);
